import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type {
  ClassSetupOptions,
  InstituteClass,
  SectionMonitorOptions,
  SuspendedClass,
  SuspendedSection,
} from '@/types/instituteClass';
import type { ClassPayload } from '../schemas/class.schema';

/**
 * Every call this feature makes. As with the rest of the institute area there is
 * no institute id in any path — the server takes it from the caller's token, so
 * there is nothing for a client to point at another institute's classes.
 *
 * The academic year is the server's decision too: a class setup is always
 * written against the institute's current session.
 */
export const classesApi = {
  list: () => unwrap(apiClient.get<ApiSuccess<InstituteClass[]>>('/institute/classes')),

  /** The subject master and the institute's own board and mediums, for the class form. */
  setupOptions: () =>
    unwrap(apiClient.get<ApiSuccess<ClassSetupOptions>>('/institute/classes/setup-options')),

  create: (payload: ClassPayload) =>
    unwrap(apiClient.post<ApiSuccess<InstituteClass>>('/institute/classes', payload)),

  update: (uuid: string, payload: ClassPayload) =>
    unwrap(apiClient.patch<ApiSuccess<InstituteClass>>(`/institute/classes/${uuid}`, payload)),

  /**
   * Only a class's three subject lists, for the Subjects page's class view.
   * The server treats each list sent as the whole list, and leaves the class's
   * sections, fees and details alone.
   */
  setSubjects: (
    uuid: string,
    lists: Pick<ClassPayload, 'coreSubjects' | 'electives' | 'languages'>,
  ) => unwrap(apiClient.patch<ApiSuccess<InstituteClass>>(`/institute/classes/${uuid}`, lists)),

  remove: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/classes/${uuid}`)),

  /** One section, leaving the class and its other sections. Refused for the last one. */
  removeSection: (sectionUuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/classes/sections/${sectionUuid}`)),

  /** A section's class monitor and the students who could be chosen. */
  sectionMonitor: (sectionUuid: string) =>
    unwrap(
      apiClient.get<ApiSuccess<SectionMonitorOptions>>(`/institute/classes/sections/${sectionUuid}/monitor`),
    ),

  /** Names, replaces or — with null — removes a section's class monitor. */
  setSectionMonitor: (sectionUuid: string, studentUuid: string | null) =>
    unwrap(
      apiClient.put<ApiSuccess<SectionMonitorOptions>>(`/institute/classes/sections/${sectionUuid}/monitor`, {
        studentUuid,
      }),
    ),

  /**
   * Which of its class's subjects one section teaches. Null puts it back to all
   * of them — the default for every section.
   */
  setSectionSubjects: (sectionUuid: string, subjectUuids: string[] | null) =>
    unwrap(
      apiClient.put<ApiSuccess<{ subjectUuids: string[] | null }>>(
        `/institute/classes/sections/${sectionUuid}/subjects`,
        { subjectUuids },
      ),
    ),

  /** Every class this institute has suspended, newest first. */
  listSuspended: () =>
    unwrap(apiClient.get<ApiSuccess<SuspendedClass[]>>('/institute/classes/suspended')),

  /** Brings a suspended class back with its sections, subjects and fees. */
  reinstate: (uuid: string) =>
    unwrap(apiClient.post<ApiSuccess<InstituteClass>>(`/institute/classes/${uuid}/reinstate`)),

  /** Sections suspended on their own this session, newest first. */
  listSuspendedSections: () =>
    unwrap(apiClient.get<ApiSuccess<SuspendedSection[]>>('/institute/classes/sections/suspended')),

  /** Brings one section back into its class, with its own subject list. */
  reinstateSection: (sectionUuid: string) =>
    unwrap(
      apiClient.post<ApiSuccess<{ uuid: string; name: string; className: string }>>(
        `/institute/classes/sections/${sectionUuid}/reinstate`,
      ),
    ),
};
